// Natural Euphoria Supplement Matcher - Modern UX refresh

const concernToCollection = {
  sleep: 'better-sleep',
  stress: 'stress-calm',
  energy: 'energy-focus',
  focus: 'energy-focus',
  recovery: 'fitness-recovery',
  digestion: 'gut-health',
  immunity: 'immune-support',
  skin: 'beauty-support',
  joints: 'joint-mobility',
  mood: 'stress-calm'
};

const insights = {
  sleep: { name: 'Sleep Support', icon: '☾', why: 'Quality sleep supports recovery, mood and daily resilience.', category: 'Rest & Recovery' },
  stress: { name: 'Stress Management', icon: '◌', why: 'Support a calmer routine with products designed around balance and relaxation.', category: 'Stress & Mood Support' },
  energy: { name: 'Energy Support', icon: '↗', why: 'Daily energy is easier to maintain when your nutrition and routine are well supported.', category: 'Energy & Performance' },
  focus: { name: 'Focus & Clarity', icon: '✦', why: 'Cognitive support may help you build a more focused, productive routine.', category: 'Focus & Brain Health' },
  recovery: { name: 'Fitness Recovery', icon: '◆', why: 'Recovery-focused support can complement training, movement and active lifestyles.', category: 'Fitness & Recovery' },
  digestion: { name: 'Digestive Support', icon: '♧', why: 'Gut health plays an important role in comfort, nutrient absorption and wellbeing.', category: 'Gut Health & Digestion' },
  immunity: { name: 'Immune Support', icon: '⬡', why: 'Immune-focused products can support your everyday wellness routine year-round.', category: 'Immunity Support' },
  skin: { name: 'Skin, Hair & Beauty', icon: '✧', why: 'Beauty support often starts from within, including hydration, collagen and nutrient support.', category: 'Skin, Hair & Beauty' },
  joints: { name: 'Joint & Mobility Support', icon: '○', why: 'Mobility support can complement movement, recovery and long-term active living.', category: 'Joint & Mobility' },
  mood: { name: 'Mood Support', icon: '♡', why: 'Emotional wellbeing benefits from consistent lifestyle habits and targeted support.', category: 'Stress & Mood Support' }
};

let userResponses = {
  concerns: [],
  activity: '',
  sleep: '',
  stress: ''
};

let shopifyProducts = {};

function getCollectionUrlFromConcern(concern) {
  const handle = concernToCollection[concern];
  if (!handle) return '/collections/all-products';
  return `/collections/${handle}`;
}

function scrollToQuestionTop() {
  const el =
    document.querySelector('#supplement-matcher .phase.active .section-title') ||
    document.querySelector('#supplement-matcher .phase.active .results-title') ||
    document.querySelector('#supplement-matcher .phase.active .disclaimer-title') ||
    document.querySelector('#supplement-matcher .content');

  if (!el) return;

  const yOffset = -80;
  const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;

  window.scrollTo({
    top: y,
    behavior: 'smooth'
  });
}

const matcherHTML = `
  <div class="matcher-wrapper">
    <div class="header">
      <div class="header-content">
        <div class="header-copy">
          <div class="matcher-kicker">Wellness Finder</div>
          <h1>Build your personalised supplement routine</h1>
          <p class="subtitle">Answer a few quick questions and we’ll match your goals with the most relevant Natural Euphoria wellness categories and products.</p>
          <div class="header-actions">
            <span class="time-estimate">⏱ Less than 60 seconds</span>
            <span class="trust-pill">No medical claims, just guided shopping</span>
          </div>
        </div>
      </div>
    </div>

    <div class="content">
      <div class="phase active" id="phaseDisclaimer">
        <div class="disclaimer-box">
          <h2 class="disclaimer-title">Before you start</h2>
          <div class="disclaimer-content">
            <p><strong>This tool is for general informational and shopping guidance only. It is not medical advice.</strong></p>
            <p>It can help you explore supplement categories based on common wellness goals, but it should not be used to diagnose, treat or manage a health condition.</p>
            <ul class="disclaimer-list">
              <li>If you have a diagnosed medical condition</li>
              <li>If you take prescription medication</li>
              <li>If you are pregnant or breastfeeding</li>
              <li>If you notice severe or persistent symptoms</li>
              <li>Before starting a new supplement routine</li>
            </ul>
          </div>
          <div class="button-group" style="justify-content:center;">
            <button class="btn-secondary" onclick="matcherExitDisclaimer()">I don’t agree</button>
            <button class="btn-primary" onclick="matcherAcceptDisclaimer()">I understand, continue →</button>
          </div>
        </div>
      </div>

      <div class="phase" id="phase1">
        ${progressMarkup('Step 1 of 2', 50)}
        <h2 class="section-title">What would you like support with?</h2>
        <p class="section-subtitle">Choose all that apply.</p>
        <div class="question-group">
          <div class="checkbox-group" id="concernsGroup">
            ${checkboxOption('concern', 'sleep', '☾', 'Sleep & relaxation', 'Falling asleep or winding down')}
            ${checkboxOption('concern', 'stress', '◌', 'Stress & calm', 'Daily pressure or busy mind')}
            ${checkboxOption('concern', 'energy', '↗', 'Energy & performance', 'Low energy or sluggish mornings')}
            ${checkboxOption('concern', 'focus', '✦', 'Focus & brain health', 'Concentration or mental clarity')}
            ${checkboxOption('concern', 'recovery', '◆', 'Fitness & recovery', 'Training or active lifestyle support')}
            ${checkboxOption('concern', 'digestion', '♧', 'Gut health & digestion', 'Bloating or digestive wellbeing')}
            ${checkboxOption('concern', 'immunity', '⬡', 'Immunity support', 'Everyday immune support')}
            ${checkboxOption('concern', 'skin', '✧', 'Skin, hair & beauty', 'Beauty-from-within support')}
            ${checkboxOption('concern', 'joints', '○', 'Joint & mobility', 'Movement and mobility support')}
            ${checkboxOption('concern', 'mood', '♡', 'Mood support', 'Emotional balance and wellbeing')}
          </div>
          <span class="selected-count" id="selectedCount">Select at least one goal</span>
        </div>
        <div class="button-group">
          <button class="btn-primary" onclick="matcherNextPhase(1)" id="next1Btn" disabled>Continue →</button>
        </div>
      </div>

      <div class="phase" id="phase2">
        ${progressMarkup('Step 2 of 2', 100)}
        <button class="back-btn" onclick="matcherPrevPhase(2)">← Back</button>
        <h2 class="section-title">Tell us about your routine</h2>

        <div class="question-group">
          <label class="question-label">How active are you?</label>
          <div class="radio-group" id="activityGroup">
            ${radioOption('activity', 'sedentary', '◦', 'Mostly sitting', 'Low movement most days')}
            ${radioOption('activity', 'light', '◌', 'Lightly active', 'Light exercise 1–3 days per week')}
            ${radioOption('activity', 'moderate', '◆', 'Moderately active', 'Exercise 3–5 days per week')}
            ${radioOption('activity', 'very-active', '▲', 'Very active', 'Regular intense training')}
          </div>
        </div>

        <div class="question-group">
          <label class="question-label">How is your sleep quality?</label>
          <div class="radio-group" id="sleepGroup">
            ${radioOption('sleep', 'poor', '☾', 'Poor', 'I struggle to fall or stay asleep')}
            ${radioOption('sleep', 'fair', '◐', 'Fair', 'Some nights are good, others not')}
            ${radioOption('sleep', 'good', '●', 'Good', 'I sleep reasonably well')}
          </div>
        </div>

        <div class="question-group">
          <label class="question-label">How do you manage stress?</label>
          <div class="radio-group" id="stressGroup">
            ${radioOption('stress', 'poor', '!', 'Often overwhelmed', 'Stress affects my day')}
            ${radioOption('stress', 'fair', '≈', 'I manage, but it’s hard', 'Some coping strategies')}
            ${radioOption('stress', 'good', '✓', 'I manage stress well', 'A steady routine works for me')}
          </div>
        </div>

        <div class="button-group">
          <button class="btn-secondary" onclick="matcherPrevPhase(2)">← Back</button>
          <button class="btn-primary" onclick="matcherGenerateResults()" id="next2Btn" disabled>Get my recommendations →</button>
        </div>
      </div>

      <div class="phase" id="phaseResults">
        <div class="results-header">
          <button class="back-btn" onclick="matcherStartOver()">← Start over</button>
          <h2 class="results-title">Your matched wellness categories</h2>
          <p class="results-subtitle" id="resultsSubtitle"></p>
        </div>
        <div id="loadingIndicator" class="loading-text">
          <span class="loading-spinner"></span>
          <span>Loading your recommendations...</span>
        </div>
        <div id="resultsContainer"></div>
      </div>
    </div>
  </div>
`;

function progressMarkup(label, percent) {
  return `
    <div class="progress-shell">
      <div class="progress-top">
        <span class="step-indicator">${label}</span>
        <span class="progress-percent">${percent}% complete</span>
      </div>
      <div class="progress-bar">
        <div class="progress-fill" style="width:${percent}%"></div>
      </div>
    </div>
  `;
}

function checkboxOption(name, value, marker, title, help) {
  return `
    <label class="checkbox-item">
      <input type="checkbox" name="${name}" value="${value}">
      <span class="option-marker">${marker}</span>
      <span class="checkbox-label">${title}<span class="option-help">${help}</span></span>
    </label>
  `;
}

function radioOption(name, value, marker, title, help) {
  return `
    <label class="radio-item">
      <input type="radio" name="${name}" value="${value}">
      <span class="option-marker">${marker}</span>
      <span class="radio-label">${title}<span class="option-help">${help}</span></span>
    </label>
  `;
}

async function fetchProductsFromCollections() {
  for (const [concern, handle] of Object.entries(concernToCollection)) {
    try {
      const response = await fetch(`/collections/${handle}.json`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();

      shopifyProducts[concern] = (data.products || []).map(product => ({
        title: product.title,
        url: product.url || `/products/${product.handle}`
      }));
    } catch (error) {
      shopifyProducts[concern] = [];
    }
  }
}

function setupPhase1() {
  document.querySelectorAll('#concernsGroup input').forEach(cb => {
    cb.addEventListener('change', () => {
      updateConcerns();
      updateCheckboxUI();
    });
  });
}

function setupPhase2() {
  document.querySelectorAll('#activityGroup input, #sleepGroup input, #stressGroup input').forEach(rb => {
    rb.addEventListener('change', function () {
      userResponses[this.name] = this.value;

      updateRadioUI('#activityGroup');
      updateRadioUI('#sleepGroup');
      updateRadioUI('#stressGroup');
      updatePhase2Button();
    });
  });
}

function updateConcerns() {
  const checked = document.querySelectorAll('#concernsGroup input:checked');
  userResponses.concerns = Array.from(checked).map(cb => cb.value);

  const nextBtn = document.getElementById('next1Btn');
  const selectedCount = document.getElementById('selectedCount');

  if (nextBtn) nextBtn.disabled = userResponses.concerns.length === 0;

  if (selectedCount) {
    selectedCount.textContent =
      userResponses.concerns.length === 0
        ? 'Select at least one goal'
        : `${userResponses.concerns.length} goal${userResponses.concerns.length === 1 ? '' : 's'} selected`;
  }
}

function updatePhase2Button() {
  const nextBtn = document.getElementById('next2Btn');

  if (nextBtn) {
    nextBtn.disabled = !userResponses.activity || !userResponses.sleep || !userResponses.stress;
  }
}

function updateCheckboxUI() {
  document.querySelectorAll('#supplement-matcher .checkbox-item').forEach(item => {
    item.classList.toggle('checked', !!item.querySelector('input:checked'));
  });
}

function updateRadioUI(groupSelector) {
  document.querySelectorAll(`${groupSelector} .radio-item`).forEach(item => {
    item.classList.toggle('checked', !!item.querySelector('input:checked'));
  });
}

function matcherAcceptDisclaimer() {
  document.getElementById('phaseDisclaimer').classList.remove('active');
  document.getElementById('phase1').classList.add('active');

  setupPhase1();
  scrollToQuestionTop();
}

function matcherExitDisclaimer() {
  alert('You need to accept the disclaimer to use the supplement matcher.');
}

function matcherNextPhase(current) {
  document.getElementById(`phase${current}`).classList.remove('active');
  document.getElementById(`phase${current + 1}`).classList.add('active');

  if (current === 1) setupPhase2();

  scrollToQuestionTop();
}

function matcherPrevPhase(current) {
  document.getElementById(`phase${current}`).classList.remove('active');
  document.getElementById(`phase${current - 1}`).classList.add('active');

  scrollToQuestionTop();
}

function matcherGenerateResults() {
  document.getElementById('phase2').classList.remove('active');
  document.getElementById('phaseResults').classList.add('active');

  const loadingIndicator = document.getElementById('loadingIndicator');
  const resultsContainer = document.getElementById('resultsContainer');

  if (loadingIndicator) loadingIndicator.style.display = 'flex';
  if (resultsContainer) resultsContainer.innerHTML = '';

  scrollToQuestionTop();

  setTimeout(buildRecommendations, 500);
}

function normaliseProductUrl(url) {
  if (!url) return '#';
  if (/^https?:\/\//i.test(url)) return url;

  return `https://naturaleuphoria.co.uk${url}`;
}

function buildRecommendations() {
  const container = document.getElementById('resultsContainer');
  if (!container) return;

  container.innerHTML = '';

  const categories = {};

  userResponses.concerns.forEach(concern => {
    const data = insights[concern];
    if (!data) return;

    if (!categories[data.category]) {
      categories[data.category] = {
        concerns: [],
        products: [],
        icon: data.icon,
        title: data.name,
        primaryConcern: concern
      };
    }

    categories[data.category].concerns.push(data);

    if (shopifyProducts[concern]) {
      categories[data.category].products.push(...shopifyProducts[concern]);
    }
  });

  const resultsSubtitle = document.getElementById('resultsSubtitle');
  if (resultsSubtitle) {
    resultsSubtitle.textContent =
      'Based on your answers, these are the wellness categories most likely to fit your routine.';
  }

  Object.keys(categories).forEach(category => {
    const data = categories[category];
    const collectionUrl = getCollectionUrlFromConcern(data.primaryConcern);

    const productsList = data.products.length
      ? data.products.slice(0, 5).map(product =>
          `<a href="${normaliseProductUrl(product.url)}" class="product-link" target="_blank" rel="noopener">${product.title} →</a>`
        ).join('')
      : `<a href="${collectionUrl}" class="product-link">Shop ${data.title} →</a>`;

    const reasons = data.concerns.map(concern =>
      `<div><strong>${concern.name}</strong><br><span>${concern.why}</span></div>`
    ).join('');

    const card = document.createElement('div');
    card.className = 'recommendation-card';

    card.innerHTML = `
      <div class="rec-header">
        <div class="rec-icon">${data.icon}</div>
        <div>
          <span class="rec-category">${category}</span>
          <div class="rec-title">${data.title}</div>
          <span class="rec-match">Matched to your answers</span>
        </div>
      </div>
      <div class="rec-body">
        <div class="rec-why">
          <span class="rec-why-label">Why this fits</span>
          <div class="rec-why-text">${reasons}</div>
        </div>
        <div class="rec-products">
          <span class="rec-products-label">Recommended next step</span>
          <div>${productsList}</div>
          <a href="${collectionUrl}" class="primary-cta">Shop ${data.title} →</a>
        </div>
      </div>
    `;

    container.appendChild(card);
  });

  const loadingIndicator = document.getElementById('loadingIndicator');
  if (loadingIndicator) loadingIndicator.style.display = 'none';
}

function matcherStartOver() {
  userResponses = {
    concerns: [],
    activity: '',
    sleep: '',
    stress: ''
  };

  document.querySelectorAll('#supplement-matcher input').forEach(input => {
    input.checked = false;
  });

  document.querySelectorAll('#supplement-matcher .checkbox-item, #supplement-matcher .radio-item').forEach(item => {
    item.classList.remove('checked');
  });

  document.querySelectorAll('#supplement-matcher .phase').forEach(phase => {
    phase.classList.remove('active');
  });

  document.getElementById('phaseDisclaimer').classList.add('active');

  const selectedCount = document.getElementById('selectedCount');
  if (selectedCount) selectedCount.textContent = 'Select at least one goal';

  const next1Btn = document.getElementById('next1Btn');
  const next2Btn = document.getElementById('next2Btn');

  if (next1Btn) next1Btn.disabled = true;
  if (next2Btn) next2Btn.disabled = true;

  scrollToQuestionTop();
}

function initMatcher() {
  const container = document.getElementById('supplement-matcher');

  if (!container) {
    console.error('Supplement matcher error: missing <div id="supplement-matcher"></div>');
    return;
  }

  container.innerHTML = matcherHTML;
  fetchProductsFromCollections();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMatcher);
} else {
  initMatcher();
}